<template>
<div class="documents-block documents">
  <preloader :visible="preloader" />
  <reviewer :pdf="reviewer" :can-review="canReview" :can-restore="canRestore" :can-sign="canSign"
            @close="onViewResult" @review="onViewerReview" @restore="onViewerRestore" @sign="onViewerSign" />

  <v-server-table v-if="columns" ref="vuetable" class="custom-table-modifier"
                  :url="route" :columns="columns" :options="options" @loading="onLoading" @loaded="onLoaded">
    <div v-if="canFilter" slot="beforeTable"><v-expansion-panels>
      <v-expansion-panel>
        <v-expansion-panel-header hide-actions>
          <span class="header">Advanced filters</span>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-row>
            <v-col cols="12" lg="4" xl="3">
              <v-date-picker range no-title scrollable v-model="options.filters.created">
                <v-text-field readonly :value="createdText" clearable @click:clear="options.filters.created=[]"></v-text-field>
              </v-date-picker>
            </v-col>
            <v-col cols="12" lg="8" xl="9">
              <v-container fluid>
                <v-row>
                  <v-col cols="12" v-if="Array.isArray(templatesList) && templatesList.length > 0">
                    <v-select :items="templatesList" v-model="selectedTemplate" @change="onTemplateSelected" clearable
                              :return-object="true" label="Template">
                      <template v-slot:append-outer><div v-if="!!selectedTemplate" class="padding-left-15">
                        <a href="#" class="btn btn-sm btn-danger" @click="onTemplateDelete"><i class="icmn-bin" aria-hidden="true"></i></a>
                      </div></template>
                    </v-select>
                  </v-col>
                  <v-col cols="12">
                    <v-select :items="modalitiesList" v-model="options.filters.types" chips deletable-chips multiple clearable
                              :return-object="false" label="Modalities"></v-select>
                  </v-col>
                  <v-col cols="12">
                    <v-combobox :items="holterCodesList" v-model="options.filters.codes" chips deletable-chips multiple clearable
                                :return-object="false" label="Primary Indication"></v-combobox>
                  </v-col>
                  <v-col cols="12">
                    <v-text-field v-model="options.filters.source_name" clearable label="Document name"></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-combobox :items="ownersList" v-model="options.filters.groups_customers" chips deletable-chips multiple clearable
                                :return-object="false" label="Physicians & Groups"></v-combobox>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12"><div class="pull-right">
                    <v-btn @click="onTemplateSave">Save</v-btn>
                    <v-btn @click="applyFilters">Apply</v-btn>
                  </div></v-col>
                </v-row>
              </v-container>
            </v-col>
          </v-row>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels></div>
    <template v-slot:user="ctx"><div class="text-left">{{ ctx.row.user.name }}
      <div v-if="ctx.row.signed_by" class="small">Signed by: {{ ctx.row.signed_by.name }}</div>
    </div></template>
    <template v-slot:source_name="ctx"><a :href="`#${ctx.row.id}`" v-on:click="showViewer($event, ctx.row)">{{ ctx.row.source_name }}</a></template>
    <template v-slot:groups_customers="ctx" v-if="canGroups">
      <div v-html="ctx.row.all_customers" class="table-span-block"></div>
      <div v-html="ctx.row.all_groups" class="table-span-block"></div>
    </template>
    <template  v-slot:created_at="ctx">
      <span v-html="momentVue(ctx.row.created_at).format(dateFormat).replace(/\s/,'<br>')"></span>
    </template>
    <template  v-slot:actions="ctx"><div class="actions-bts pull-left">
      <a v-if="canEdit || ctx.row.user_id === user" class="mr-2 mb-2 btn btn-sm btn-primary"
         :href="`/document/${ctx.row.id}/edit`"><i class="icmn-pencil3" aria-hidden="true"></i></a>
      <a v-if="canDelete || ctx.row.user_id === user" class="mr-2 mb-2 btn btn-sm btn-danger"
         href="#delete" v-on:click.prevent="tryDelete(ctx.row.id)"><i class="icmn-bin" aria-hidden="true"></i></a>
      <a v-if="canShare || ctx.row.user_id === user" class="mr-2 mb-2 btn btn-sm btn-primary custom-btn-loader"
         href="#share" v-on:click.prevent="tryShare(ctx.row.id)"><i class="icmn-share2" aria-hidden="true"></i></a>
      <a v-if="canFax || ctx.row.user_id === user" :class="'mr-2 mb-2 btn btn-sm custom-btn-loader ' + getFaxColor(ctx.row)"
         href="#fax" v-on:click.prevent="trySendFax(ctx.row.id)"><i class="icmn-printer" aria-hidden="true"></i></a>
      <a v-if="(ctx.row.status === 'new' || ctx.row.status === 'reviewed') && (canReview || canSign)"
         :class="'mr-2 mb-2 btn btn-sm custom-btn-loader' + (ctx.row.status === 'new' ? ' btn-success' : '')"
         href="#view" v-on:click.prevent="switchReviewed($event, ctx.row.id)"><i class="icmn-eye" aria-hidden="true"></i></a>
      <a v-if="(ctx.row.status === 'new' || ctx.row.status === 'reviewed') && canSign"
         class="mr-2 mb-2 btn btn-sm btn-success custom-btn-loader"
         href="#sign" v-on:click.prevent="trySign($event, ctx.row.id)"><i class="icmn-quill3" aria-hidden="true"></i></a>
    </div></template>
  </v-server-table>
</div>
</template>

<script>
import Preloader from "../blocks/preloader";
import Reviewer from "./PdfJsViewer.vue";
import {CvConsts} from "../../cv-consts";
import "../../cv-cache"

export default {
  components: { Preloader, Reviewer },
  props: {
    route: {
      type: String
    },
    group: {
      type: Number
    },
    user: {},
    owners: {
      type: Array|String,
      default: () => { return []; }
    },
    canCreate: {
      type: Boolean,
      default: false
    },
    canEdit: {
      type: Boolean,
      default: false
    },
    canDelete: {
      type: Boolean,
      default: false
    },
    canShare: {
      type: Boolean,
      default: false
    },
    canFax: {
      type: Boolean,
      default: false
    },
    canGroups: {
      type: Boolean,
      default: false
    },
    canUpdate: {
      type: Boolean,
      default: false
    },
    canReview: {
      type: Boolean,
      default: false
    },
    canRestore: {
      type: Boolean,
      default: false
    },
    canSign: {
      type: Boolean,
      default: false
    },
    canFilter: {
      type: Boolean,
      default: false
    },
    dateFormat: {
      type: String,
      default: 'MM-DD-YYYY'
    },
    appUrl: {
      type: String,
    }
  },
  data() {
    return {
      preloader: false,
      reviewer: false,
      modalitiesList: CvConsts.Modalities,
      holterCodesList: CvConsts.HolterCodes,
      techniciansList: [],
      ownersList: [],
      templatesList: [],
      selectedTemplate: '',
      filterDatesMenu: false,
      columns: this.canGroups
          ? [ 'created_at', 'user', 'source_name', 'groups_customers', 'actions' ]
          : [ 'created_at', 'user', 'source_name', 'actions' ],
      options: {
        columnsClasses: {
          source_name: 'ignore-custom-table-modifier',
          groups_customers: 'hide-column-tablet'
        },
        filterByColumn: true,
        filters: {
          user: [],
          source_name: '',
          groups_customers: [],
          created: [],
          created_at: {},
          types: [],
          codes: [],
        },
        debounce: 1000,
        requestFunction: (data) => {
          const customFilters = this.canFilter ? Object.fromEntries(Object.entries(this.options.filters).filter(([k,v]) => (v != null && v !== ''))) : {};
          data['query'] = 'query' in data ? {
            ...data.query,
            ...customFilters
          } : customFilters;
          this.filteringParams = data;
          return axios.get(this.route, { params: this.filteringParams });
        },
        dateColumns:[ 'created_at' ],
        dateFormat: this.dateFormat,
        datepickerOptions: {
          showDropdowns: true,
          autoUpdateInput: true,
        },
        orderBy: {
          ascending: false,
        },
        perPage: 30,
        pagination: {
          chunk:30,
          dropdown:false
        },
        sortable: [],
        filterable: this.canFilter ? [] : [ 'user', 'source_name', 'groups_customers', 'created_at' ],
        headings: {
          user: 'Technician',
          source_name: 'Document',
          groups_customers: 'Users',
          created_at: 'Created',
          actions: 'Actions',
        }
      }
    }
  },
  computed: {
    createdText() {
      return this.options.filters.created.join(' ~ ');
    }
  },
  methods: {
    momentVue(...args) {
      return moment(...args);
    },
    momentDiff(timestamp) {
      return moment().diff(moment.utc(timestamp), 'minutes');
    },
    momentFromNow(timestamp) {
      return moment.utc(timestamp).fromNow();
    },
    onLoading(e) {
      this.preloader = true;
    },
    onLoaded(e) {
      this.preloader = false;
    },
    loadTemplates() {
      window.cvCache.get('ReportTemplates').then(r => {
        this.onTemplatesLoaded(r);
      });
    },
    onTemplatesLoaded(data) {
      if (data) this.templatesList = JSON.parse(data);
    },
    loadOwners() {
      if (typeof(this.owners) !== 'string') {
        this.onOwnersLoaded(this.owners);
        return;
      }

      axios.get(this.owners, {}).then(r => {
        this.onOwnersLoaded(r.data);
      }).catch(e => {
        console.error(e);
      });
    },
    onOwnersLoaded(data) {
      this.ownersList = data;
    },

    onTemplateSave() {
      const self = this;
      this.$swal.fire({
        title: 'Saving template...',
        input: 'text',
        inputLabel: 'Name',
        confirmButtonText: 'Save',
        showCloseButton: true,
        showCancelButton: true,
        cancelButtonText: 'Cancel'
      }).then((result) => {
        if (!result.isConfirmed || !result.value) return;

        const entry = {
          text: result.value,
          value: JSON.parse(JSON.stringify(self.options.filters))
        };
        let item = self.templatesList.find((v) => v.text === entry.text);
        if (item) item.value = entry.value;
        else self.templatesList.push(entry);

        window.cvCache.put('ReportTemplates', JSON.stringify(self.templatesList));
      });
    },
    onTemplateSelected(data) {
      if (data) this.options.filters = JSON.parse(JSON.stringify(data.value));
    },
    onTemplateDelete() {
      if (this.selectedTemplate) {
        const name = this.selectedTemplate.text;
        this.templatesList = this.templatesList.filter(v => v.text !== name);
        window.cvCache.put('ReportTemplates', JSON.stringify(this.templatesList));
      }
    },
    applyFilters() {
      this.$refs.vuetable.getData();
    },

    getFaxColor(doc) {
      if ('faxes' in doc && doc.faxes.length > 0) {
        const last = doc.faxes[doc.faxes.length - 1];
        if (last.status === 'fail') {
          console.warn(`Document #${doc.id} - ${doc.source_name} was not faxed because of '${last.details}'`);
          return 'btn-danger';
        }
        if (last.status === 'sent') return 'btn-success';
        return 'btn-warning';
      }
      return 'btn-primary';
    },

    trySendFax(documentId) {
      this.$swal.fire({
        title: 'Fax document...',
        inputLabel: 'Fax number',
        input: 'text',
        inputValidator: (value) => {
          return new Promise((resolve) => {
            return (value && value.length === 11) ? resolve() : resolve('Fax number is required and must be 11 digits.');
          });
        },
        confirmButtonText: 'Send',
        showCloseButton: true,
        showCancelButton: true,
        cancelButtonText: 'Cancel'
      }).then((result) => {
        if (result.isConfirmed) {
          this.sendFax(documentId, result.value)
        }
      });
    },
    sendFax(id, fax) {
      const self = this;
      axios.post(`/document/${id}/fax`, { fax: fax }).then(response => {
        if (response.data.error) {
          self.toast(response.data.error, 'error');
        } else {
          self.toast('Document sent.');
        }
      }).catch(e => {
        console.log(e);
        self.toast('Error occurred while sending document.', 'error');
      });
    },

    tryDelete(documentId) {
      this.$swal.fire({
        title: 'Are you sure?',
        text: 'You will not be able to recover this document!',
        icon: 'warning',
        confirmButtonText: 'Delete',
        showCloseButton: true,
        showCancelButton: true,
        cancelButtonText: 'Cancel'
      }).then((result) => {
        if (result.isConfirmed) {
          this.deleteDocument(documentId);
        }
      });
    },
    deleteDocument(id) {
      const self = this;
      axios.post(`/document/${id}/delete`).then(response => {
        if (response.data.error) {
          self.toast(response.data.error, 'error');
        } else {
          self.toast(response.data.success);
          this.$refs.vuetable.getData()
        }
      }).catch(e => {
        console.log(e);
        self.toast('Document was not deleted because of error.', 'error');
      });
    },

    switchReviewed(e, documentId) {
      let el = $(e.target instanceof HTMLAnchorElement ? e.target: e.target.parentElement);
      const status = el.hasClass('btn-success') ? 'reviewed' : 'new';
      this.trySwitchStatus(documentId, status);
    },
    trySign(e, documentId) {
      this.trySwitchStatus(documentId, 'signed');
    },
    askSignPassword() {
      return this.$swal.fire({
        title: 'Sign a document...',
        inputLabel: 'Password',
        input: 'password',
        inputValidator: (value) => {
          return new Promise((resolve) => { return (value && value.length > 0) ? resolve() : resolve('Password is required'); });
        },
        confirmButtonText: 'Sign',
        showCloseButton: true,
        showCancelButton: true,
        cancelButtonText: 'Cancel'
      }).then(
          result => { return result.isConfirmed ? Promise.resolve(result.value) : Promise.reject('Cancelled'); },
          error => { return Promise.reject(error) }
      );
    },
    signFile(id, password) {
      return axios.post(`/document/${id}/sign`, { cert_pass: password }).then(response => {
        return response.data.error ? Promise.reject(response.data.error) : Promise.resolve(response.data.message);
      }, error => {
        return Promise.reject(error);
      });
    },

    tryShare(documentId) {
      const self = this;
      this.$swal.fire({
        title: 'Share document...',
        inputLabel: 'E-mail',
        input: 'email',
        inputValidator: (value) => { return new Promise((resolve) => {
            return self.checkEmail(value) ? resolve() : resolve('Valid e-mail required');
          });
        },
        confirmButtonText: 'Share',
        showCloseButton: true,
        showCancelButton: true,
        cancelButtonText: 'Cancel'
      }).then((result) => {
        if (result.isConfirmed) {
          console.debug(result);
          this.shareDocument(documentId, result.value)
        }
      });
    },
    shareDocument(id, email) {
      const self = this;
      axios.get(`/document/${id}/share`).then(response => {
        if (!response.data || !response.data.token) {
          self.toast('Cant get document token.', 'error');
          return;
        }

        const link = `${self.appUrl}/document/${response.data.token}`;
        axios.post(`/document/${id}/share/${response.data.id}`, { email: email }).then(response => {
          if (response.data.error) self.toast(response.data.error, 'error');
          else self.toast('Document shared');
        }).catch(e => {
          console.log(e);
          self.toast('Document was not shared because of error.', 'error');
        });

      }).catch(e => {
        console.log(e);
        self.toast('Document was not shared because of error.', 'error');
      });
    },
    checkEmail(value) {
      return /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(value);
    },

    showViewer(e, document) {
      e.preventDefault();
      const self = this;
      axios.get(`/document/${document.id}/download`, { responseType: 'arraybuffer' })
        .then(r => r.data)
        .then(data => {
          const name = document.source_name || 'unnamed.pdf';
          self.reviewer = {
            id: document.id,
            promise: Promise.resolve(data),
            name,
            save: self.onViewerSave,
            error: () => {
                window.saveBlobAs(data, name)
            }
          };
        })
        .catch(console.error);
    },
    onViewerSave(document, content) {
      const self = this;
      const data = new FormData();
      data.append('document', new Blob([content.byteLength ? content : content.buffer]), document.name);
      if ('notes' in document) data.append('notes', document.notes);
      return axios.post(`/document/${document.id}/update/api`, data).then(r => {
        if (r.data) {
          if (r.data.error) {
            self.toast(r.data.error, 'error');
            return Promise.reject(r.data.error);
          }
          if (r.data.message) {
            self.toast(r.data.message);
            return Promise.resolve(r.data.id);
          }
        }
        self.toast('Unknown error', 'error');
        return Promise.reject('Unknown error');
      });
    },
    onViewerReview(document) {
      this.trySwitchStatus(document.id, 'reviewed');
    },
    onViewerRestore(document) {
      this.trySwitchStatus(document.id, 'new');
    },
    onViewerSign(document) {
      return this.trySwitchStatus(document.id, 'signed');
    },
    onViewResult(document, wasChanged) {
      if (wasChanged) this.$refs.vuetable.getData();
    },
    trySwitchStatus(documentId, status) {
      const self = this;
      let result = (status === 'signed')
          ? this.askSignPassword().then(password => {
              return self.signFile(documentId, password);
            }).then(message => {
              self.toast(message);
              return Promise.resolve();
            })
          : this.switchStatus(documentId, status);

      return result.then(() => {
        self.$refs.vuetable.getData();
        self.toast(`Report #${documentId} status changed to ${status}.`);
      }).catch(error => {
        console.error(error);
        self.toast(error, 'error');
      });
    },
    switchStatus(documentId, status) {
      return axios.post(`/document/${documentId}/status`, { status }).then(
        response => { return response.data.error ? Promise.reject(response.data.error) : Promise.resolve(); },
        error => { return Promise.reject(error) }
      );
    },

    toast(message, type) {
      const self = this.$swal;
      self.fire({
        text: message,
        icon: type || 'success',
        toast: true,
        showConfirmButton: false,
        position: 'top-end',
        timer: 1500,
        didOpen: (toast) => {
          toast.addEventListener('mouseenter', self.stopTimer)
          toast.addEventListener('mouseleave', self.resumeTimer)
        }
      });
    }
  },
  mounted() {
    this.loadTemplates();
    this.loadOwners();
  }
}
</script>
